import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {AuthRestService} from 'app/core/modules/rest/authentication/auth-rest.service';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {PermissionsPopupComponent} from '../popups/permissions-popup/permissions-popup.component';
import {Store} from '@ngrx/store';
import {AppState} from '@core/reducers';
import {UserDataModel} from '@core/modules/rest/user/response/user-data.model';
import {MoreInfoPopupComponent} from '../popups/more-info-popup/more-info-popup.component';
import {YesNoDialogComponent, YesNoDialogData} from '@shared/dialogs/yes-no-dialog/yes-no-dialog.component';
import {TranslateService} from '@ngx-translate/core';
import {AlterUserActiveStatusRequestModel} from '@core/modules/rest/user/request/alter-user-active-status-request.model';
import {AlterUserStatus, DeleteUser, UpdateUserRoles} from '../../store/user-management-actions';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {IndicationRestService} from '@core/modules/rest/indication/indication-rest.service';
import {ConsentUseCaseRestService} from '@core/modules/rest/consent-use-case/consent-use-case-rest.service';
import {EditRolesPopupComponent} from '../popups/edit-roles-popup/edit-roles-popup.component';
import {KeycloakApplicationRolesEnum} from '@core/modules/rest/user/enums/keycloak-application-roles.enum';
import {UserRestService} from '@core/modules/rest/user/user-rest.service';
import {SaveUserRolesRequestModel} from '../popups/edit-roles-popup/model/save-user-roles-request.model';
import {ConsentUseCaseBasicModel} from '@core/modules/rest/consent-use-case/response/consent-use-case-basic.model';
import {UserSettingsModel} from '@core/modules/rest/user/response/user-settings.model';
import {DropdownModel} from '@shared/components/dropdown-button/model/dropdown.model';
import {ButtonActionsEnum} from '@shared/components/dropdown-button/enum/button-actions.enum';
import {KeycloakService} from 'keycloak-angular';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Router} from '@angular/router';
import {NotificationService} from '@shared/components/notification/service/notification.service';
import {EnvironmentService} from '@core/environment/environment.service';
import {LoggedUserDataModel} from '@core/modules/rest/user/response/logged-user-data.model';
import {IndicationBasicWithChapterNumberModel} from '@core/modules/rest/indication/response/indication-basic-with-chapter-number.model';
import {PersonalSettingsModel} from '@shared/model/personal-settings.model';

@UntilDestroy()
@Component({
    selector: 'tr[user-item]',
    templateUrl: './user-item.component.html',
    styleUrls: ['./user-item.component.scss']
})

export class UserItemComponent implements OnInit {
    @Input() user: UserDataModel;
    @Input() indications: IndicationBasicWithChapterNumberModel[];
    @Input() useCases: ConsentUseCaseBasicModel[];
    @Input() disabled = false;
    @Input() saveDisabled: boolean;
    @Input() organizationDefaultSettings: UserSettingsModel;
    @Input() loggedUserData: LoggedUserDataModel;
    @Input() filterText: string;

    @Output() educationPopUpEvent: EventEmitter<string> = new EventEmitter<string>();
    @Output() hasEducationEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

    dialogConfig = new MatDialogConfig();
    type: string;
    iconUserActivate = 'person';
    iconUserDeactivate = 'person_off';
    classUserDeactivated = 'deactivated';

    iconPersonStatus: string;
    classPersonStatus: string;
    selectedRoles: KeycloakApplicationRolesEnum[] = [];
    patientRole = KeycloakApplicationRolesEnum.PATIENT;
    doctorRole = KeycloakApplicationRolesEnum.DOCTOR;
    adminRole = KeycloakApplicationRolesEnum.ADMIN;
    editorRole = KeycloakApplicationRolesEnum.CONTENTEDITOR;
    currentLoggedUser: boolean;
    hasEducation: boolean;
    selectedPatientRole: boolean;

    menuItems: DropdownModel[] = [];
    triggerData: [{ triggerBtnIcon: string; triggerBtnClasses: string; triggerBtnIconSize: string; triggerBtnAriaLabel: any; triggerBtnIconColor: string }];

    constructor(private store: Store<AppState>, private authService: AuthRestService, private dialog: MatDialog, private translateService: TranslateService,
                private userService: UserRestService, private indicationRestService: IndicationRestService, private consentUseCaseRestService: ConsentUseCaseRestService,
                private translate: TranslateService, private keycloakService: KeycloakService, private router: Router,
                private notificationService: NotificationService, private environmentService: EnvironmentService, private httpClient: HttpClient) {
    }

    ngOnInit(): void {
        this.iconPersonStatus = this.user.active ? this.iconUserDeactivate : this.iconUserActivate;
        this.classPersonStatus = this.user.active ? '' : this.classUserDeactivated;
        const enumValues = Object.values(KeycloakApplicationRolesEnum);
        const roles = this.user.roles.map(role => role);
        this.selectedRoles = roles.sort((a, b) => enumValues.indexOf(a) - enumValues.indexOf(b));
        const personalSettings: PersonalSettingsModel = JSON.parse(this.user.personalSettings);
        if (personalSettings && personalSettings.indicationId) {
            this.hasEducation = true;
            this.hasEducationEvent.emit(this.hasEducation);
        } else {
            this.hasEducation = false;
            this.hasEducationEvent.emit(this.hasEducation);
        }
        roles.forEach(role => {
            this.selectedPatientRole = role === this.patientRole;
        });

        this.triggerData = [{
            triggerBtnClasses: 'icon',
            triggerBtnIcon: 'more_horiz',
            triggerBtnIconColor: 'gray-07',
            triggerBtnIconSize: '52',
            triggerBtnAriaLabel: this.translateService.instant('SCREEN_READER.ACTIVATE_DEACTIVATE_USER')
        }];

        const impersonateAction: DropdownModel = {
            key: ButtonActionsEnum.Consent,
            btnCssClasses: 'small outlined dropdown-item',
            btnIcon: 'supervisor_account',
            btnIconSize: '60',
            btnIconColor: 'gray-06',
            btnLabelColor: 'gray-06',
            btnLabel: this.translateService.instant('GENERAL.IMPERSONATE'),
            btnAriaLabel: this.translateService.instant('GENERAL.IMPERSONATE') + this.translateService.instant('GENERAL.BUTTON'),
            disabled: !this.hasEducation || (this.hasEducation && this.selectedPatientRole === false) || !this.user.active
        };

        const reinviteAction: DropdownModel = {
            key: ButtonActionsEnum.Reinvite,
            btnCssClasses: 'small outlined dropdown-item',
            btnIcon: 'replay',
            btnIconSize: '60',
            btnIconColor: 'gray-06',
            btnLabelColor: 'gray-06',
            btnLabel: this.translateService.instant('GENERAL.REINVITE'),
            btnAriaLabel: this.translateService.instant('GENERAL.REINVITE') + this.translateService.instant('GENERAL.BUTTON'),
            disabled: !this.hasEducation || this.user.registered || !this.user.active
        };

        const deactivateUserAction: DropdownModel = {
            key: ButtonActionsEnum.Deactivate,
            btnCssClasses: 'small outlined dropdown-item',
            btnIcon: this.iconPersonStatus,
            btnIconSize: '',
            btnIconColor: 'gray-06',
            btnLabelColor: 'gray-06',
            btnLabel: this.btnLabelText(),
            btnAriaLabel: this.btnLabelText(),
            disabled: !this.hasEducation
        };

        const deleteUserAction: DropdownModel = {
            key: ButtonActionsEnum.Delete,
            btnCssClasses: 'small outlined dropdown-item',
            btnIcon: 'delete',
            btnIconSize: '',
            btnIconColor: 'red-07',
            btnLabelColor: 'red-07',
            btnLabel: this.translateService.instant('GENERAL.DELETE'),
            btnAriaLabel: this.translateService.instant('GENERAL.DELETE') + this.translateService.instant('GENERAL.BUTTON'),
            disabled: false
        };

        this.currentLoggedUser = this.user.userId === this.loggedUserData.userId;
        if (!this.currentLoggedUser) {
            this.menuItems = [impersonateAction, reinviteAction, deactivateUserAction, deleteUserAction];
        }

    }

    btnLabelText(): string {
        if (this.user.active) {
            return this.translateService.instant('DIALOGS.DEACTIVATE_USER.YES_DEACTIVATE');
        } else {
            return this.translateService.instant('DIALOGS.DEACTIVATE_USER.YES_ACTIVATE');
        }
    }

    onOpenPopUpPermissions(user: UserDataModel): void {
        const dialogConfig = this.getDefaultDialogConfig();
        dialogConfig.data = {
            user,
            indications: this.indications,
            useCases: this.useCases,
            organizationDefaultSettings: this.organizationDefaultSettings
        };
        const dialogRef = this.dialog.open(PermissionsPopupComponent, dialogConfig);
        dialogRef.componentInstance.saveSettingsEvent.pipe(untilDestroyed(this)).subscribe(() => {
            this.notificationService.success(this.translateService.instant('NOTIFICATION.SETTINGS_UPDATED'));
        });

    }

    onOpenPopUpMoreInfo(userId: string): void {
        const dialogConfig = this.getDefaultDialogConfig();
        dialogConfig.data = userId;
        const dialogRef = this.dialog.open(MoreInfoPopupComponent, dialogConfig);
        dialogRef.componentInstance.closeDialogEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
        });
    }

    private getDefaultDialogConfig(): MatDialogConfig {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.disableClose = false;
        return dialogConfig;
    }

    onEditUserRoles(userId: string): void {
        const dialogConfig = this.getDefaultDialogConfig();
        dialogConfig.data = this.selectedRoles;
        const dialogRef = this.dialog.open(EditRolesPopupComponent, dialogConfig);
        dialogRef.componentInstance.closeDialogEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
        });

        dialogRef.componentInstance.saveRolesEvent.pipe(untilDestroyed(this)).subscribe((roles: KeycloakApplicationRolesEnum[]) => {
            const request: SaveUserRolesRequestModel = {
                userId,
                roles
            };
            this.saveUserRoles(request);
            dialogRef.close();
        });
    }

    onDeleteUser(userId: string): void {
        const data: YesNoDialogData = {
            icon: 'delete',
            iconColor: 'error',
            title: this.translateService.instant('DIALOGS.DELETE_USER.TITLE', {name: this.user.username}),
            message: this.translateService.instant('GENERAL.ARE_YOU_SURE_DELETE_DESCRIPTION'),
            yesButtonText: this.translateService.instant('DIALOGS.DELETE_USER.YES_DELETE'),
            noButtonText: this.translateService.instant('DIALOGS.DELETE_USER.NO')
        };

        const dialogConfig = this.getDefaultDialogConfig();
        dialogConfig.data = data;
        const dialogRef = this.dialog.open(YesNoDialogComponent, dialogConfig);
        dialogRef.componentInstance.noEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
        });

        dialogRef.componentInstance.yesEvent.pipe(untilDestroyed(this)).subscribe(() => {
            this.deleteUser(userId);
            dialogRef.close();
        });
    }


    onAlterUserActiveStatus(userId: string, status: boolean): void {
        let title;
        let message;
        let yesButtonText;
        let icon;
        if (status) {
            title = this.translateService.instant('DIALOGS.DEACTIVATE_USER.TITLE_ACTIVATE', {username: this.user.username});
            message = this.translateService.instant('DIALOGS.DEACTIVATE_USER.MESSAGE_ACTIVATE');
            yesButtonText = this.translateService.instant('DIALOGS.DEACTIVATE_USER.YES_ACTIVATE');
            icon = 'person';
        } else {
            title = this.translateService.instant('DIALOGS.DEACTIVATE_USER.TITLE_DEACTIVATE', {username: this.user.username});
            message = this.translateService.instant('DIALOGS.DEACTIVATE_USER.MESSAGE_DEACTIVATE');
            yesButtonText = this.translateService.instant('DIALOGS.DEACTIVATE_USER.YES_DEACTIVATE');
            icon = 'person_off';
        }

        const data: YesNoDialogData = {
            icon,
            iconColor: 'warning',
            title,
            message,
            yesButtonText,
            noButtonText: this.translateService.instant('DIALOGS.DEACTIVATE_USER.NO')
        };

        const dialogConfig = this.getDefaultDialogConfig();
        dialogConfig.data = data;
        const dialogRef = this.dialog.open(YesNoDialogComponent, dialogConfig);
        dialogRef.componentInstance.noEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
        });
        dialogRef.componentInstance.yesEvent.pipe(untilDestroyed(this)).subscribe(() => {
            this.alterActiveStatus(userId, status);
            dialogRef.close();
        });
    }

    private alterActiveStatus(userId: string, status: boolean): any {
        const request: AlterUserActiveStatusRequestModel = {
            userId,
            activeStatus: status
        };
        this.store.dispatch(AlterUserStatus({request}));
    }

    private deleteUser(userId: string): any {
        this.store.dispatch(DeleteUser({userId}));
    }

    private saveUserRoles(request: SaveUserRolesRequestModel): void {
        this.store.dispatch(UpdateUserRoles({request}));
    }

    buttonActions(event): void {
        if (event === ButtonActionsEnum.Deactivate) {
            this.onAlterUserActiveStatus(this.user.userId, !this.user.active);
        } else if (event === ButtonActionsEnum.Delete) {
            this.onDeleteUser(this.user.userId);
        } else if (event === ButtonActionsEnum.Consent) {
            this.onStartUsersConsent(this.user.kcUserId);
        } else if (event === ButtonActionsEnum.Reinvite) {
            this.onReinviteUser(this.user.userId);
        }
    }

    async onStartUsersConsent(kcUserId: string): Promise<void> {
        await this.keycloakService.getToken().then(loggedUserToken => {

            const impersonateURL = this.environmentService.authServerApiUrl + '/auth/admin/realms/HealthBank/users/' + kcUserId + '/impersonation';
            this.httpClient.post(
                impersonateURL,
                null,
                this.getHeaderWithToken(loggedUserToken)
            ).subscribe(() => {
                window.location.href = window.origin + '/#/secured';
            });
        });
    }

    getHeaderWithToken(token: string): any {
        return {
            headers: new HttpHeaders()
                .append('Content-Type', 'application/x-www-form-urlencoded2')
                .append('Authorization', 'Bearer ' + token),
            observe: 'response',
            withCredentials: true
        };
    }

    iconsForOtherRoles(icon: string): any {
        switch (icon) {
            case this.doctorRole:
                return 'doctor';
            case this.adminRole:
                return 'admin';
            case this.editorRole:
                return 'editor';
        }
    }

    onReinviteUser(userId: string): void {
        this.userService.reinviteUser(userId).pipe(untilDestroyed(this)).subscribe(() => {
            this.notificationService.success(this.translateService.instant('NOTIFICATION.EMAIL_INVITATION_SENT'));
        }, _ => {
            this.notificationService.warn(this.translateService.instant('NOTIFICATION.SOMETHING_WENT_WRONG'));
        });
    }
}

